"use client";

import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useAllLeagues from "@/hooks/useAllLeagues";
import ChevronDownSvg from "@/assets/images/lu-chevron-down.svg";

export default function LeagueFilterV7({
  selectedIds = [],
  onChange,
  className = "",
  anchorRef,
  scopeClassName = "",
  iconSrc,
  themeStyle,
}) {
  const { leagues, loading } = useAllLeagues();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [tempSelectedIds, setTempSelectedIds] = useState(selectedIds || []);
  const [viewMode, setViewMode] = useState("league");
  const triggerRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    setTempSelectedIds(selectedIds || []);
  }, [selectedIds]);

  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const padding = 8;
    const isMobile = window.innerWidth < 768;
    setDropdownStyle({
      position: "fixed",
      boxSizing: "border-box",
      top: triggerRect.bottom + gap,
      ...(isMobile
        ? { left: padding, right: padding, width: window.innerWidth - padding * 2 }
        : { right: window.innerWidth - triggerRect.right, width: 550 }),
      zIndex: 99999,
    });
  };

  useLayoutEffect(() => {
    if (open && triggerRef.current) updateDropdownPosition();
  }, [open]);

  const normalizedLeagues = useMemo(
    () =>
      (leagues || []).map((l) => ({
        ...l,
        country: l.country || l.countryName || "Khác",
      })),
    [leagues],
  );

  const filteredLeagues = useMemo(() => {
    if (!keyword.trim()) return normalizedLeagues || [];
    const lower = keyword.toLowerCase();
    return (normalizedLeagues || []).filter((league) => {
      const nameVN = (league.nameVN || "").toLowerCase();
      const name = (league.name || "").toLowerCase();
      const country = (league.country || "").toLowerCase();
      return (
        nameVN.includes(lower) || name.includes(lower) || country.includes(lower)
      );
    });
  }, [normalizedLeagues, keyword]);

  const leaguesByCountry = useMemo(() => {
    const groups = {};
    for (const league of filteredLeagues) {
      const countryKey = league.country || "Khác";
      if (!groups[countryKey]) {
        groups[countryKey] = {
          country: countryKey,
          countryLogo: league.countryLogo || null,
          items: [],
        };
      }
      groups[countryKey].items.push(league);
    }
    return Object.values(groups);
  }, [filteredLeagues]);

  const handleToggleLeague = (id) => {
    setTempSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const allIds = (filteredLeagues || []).map((l) => l.id);
    setTempSelectedIds(allIds);
  };

  const handleClearAll = () => {
    setTempSelectedIds([]);
  };

  const handleConfirm = () => {
    onChange?.(tempSelectedIds);
    setOpen(false);
  };

  const hasSelection = selectedIds && selectedIds.length > 0;
  const triggerLabel = hasSelection
    ? `${selectedIds.length} giải đấu`
    : "Tất cả giải đấu";

  return (
    <div className={`relative inline-block ${scopeClassName} ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen((prev) => {
            if (prev) return false;
            updateDropdownPosition();
            return true;
          });
        }}
        className="appearance-none m-0 box-border flex h-10 min-h-[40px] select-none items-center justify-between gap-x-2 rounded-md border border-scale-border kbv-gs-scale-border bg-[var(--theme-foreground)] px-3 py-1 w-full text-[var(--theme-text)] md:h-14 md:min-h-[56px]"
      >
        <div className="flex w-[90%] min-w-0 items-center gap-x-2">
          <span className="pointer-events-none shrink-0">
            {iconSrc ? (
              <img
                alt="icon"
                loading="lazy"
                width={24}
                height={24}
                className="pointer-events-none block h-6 w-6 min-w-[24px] object-contain"
                src={iconSrc}
              />
            ) : (
              <span className="flex h-6 w-6 min-w-[24px] items-center justify-center rounded bg-[var(--theme-accent)] text-[var(--theme-text-muted)]">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </span>
            )}
          </span>
          <span className="pointer-events-none truncate text-sm font-medium text-[var(--theme-text)] md:text-base">
            {triggerLabel}
          </span>
        </div>
        <img
          src={ChevronDownSvg}
          alt=""
          className={`block h-4 w-4 min-w-[16px] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            style={{ ...themeStyle, ...dropdownStyle }}
            className={`${scopeClassName} box-border flex max-h-[60vh] flex-col rounded-xl border border-[var(--theme-border)] bg-[var(--theme-foreground)] text-[var(--theme-text)] shadow-xl`}
          >
            <div className="box-border flex shrink-0 items-center justify-between px-3 pb-1.5 pt-2">
              <div className="relative min-w-0 flex-1">
                <input
                  type="text"
                  placeholder="Tìm tên giải đấu"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="appearance-none m-0 box-border w-full rounded-lg border border-[var(--theme-border)] bg-[var(--theme-bg)] py-2 pl-2.5 pr-9 text-sm outline-none focus:border-[var(--theme-primary)]"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)]">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="appearance-none -m-2 ml-2 rounded p-2 hover:bg-[var(--theme-bg)] border-none bg-transparent cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="box-border shrink-0 border-b border-[var(--theme-border)] px-3 pb-2">
              <div className="flex items-center justify-between text-xs uppercase">
                <span className="font-medium text-[var(--theme-text-muted)]">Xem theo</span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("league")}
                    className={`appearance-none m-0 box-border rounded px-3 py-1.5 text-xs font-medium cursor-pointer ${
                      viewMode === "league"
                        ? "border border-[var(--theme-primary)] bg-[var(--theme-foreground)] text-[var(--theme-primary)]"
                        : "border border-[var(--theme-border)] bg-[var(--theme-bg)] text-[var(--theme-text-muted)]"
                    }`}
                  >
                    Giải đấu
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("country")}
                    className={`appearance-none m-0 box-border rounded px-3 py-1.5 text-xs font-medium cursor-pointer ${
                      viewMode === "country"
                        ? "border border-[var(--theme-primary)] bg-[var(--theme-foreground)] text-[var(--theme-primary)]"
                        : "border border-[var(--theme-border)] bg-[var(--theme-bg)] text-[var(--theme-text-muted)]"
                    }`}
                  >
                    Quốc gia
                  </button>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto py-2 pl-1 pr-2">
              {loading ? (
                <div className="py-8 text-center text-sm text-[var(--theme-text-muted)]">
                  Đang tải danh sách giải đấu...
                </div>
              ) : filteredLeagues.length === 0 ? (
                <div className="py-8 text-center text-sm text-[var(--theme-text-muted)]">
                  Không tìm thấy giải đấu nào
                </div>
              ) : viewMode === "league" ? (
                <div className="grid grid-cols-2 gap-1.5">
                  {filteredLeagues.map((league) => {
                    const checked = tempSelectedIds.includes(league.id);
                    const displayName = league.nameVN || league.name || "";
                    return (
                      <button
                        key={league.id}
                        type="button"
                        onClick={() => handleToggleLeague(league.id)}
                        className="appearance-none m-0 box-border flex min-w-0 items-center gap-1.5 rounded border border-[var(--theme-border)] bg-[var(--theme-bg)] py-1.5 pl-0.5 pr-1.5 text-left text-xs transition-colors hover:bg-[var(--theme-accent)]/50 cursor-pointer"
                      >
                        <span
                          className={`box-border flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[9px] font-bold ${
                            checked
                              ? "border-[#2563eb] bg-[#2563eb] text-white"
                              : "border-[var(--theme-border)] bg-[var(--theme-foreground)]"
                          }`}
                        >
                          {checked ? "✓" : ""}
                        </span>
                        {league.logo && (
                          <img
                            src={league.logo}
                            alt=""
                            className="block h-5 w-5 shrink-0 object-contain"
                          />
                        )}
                        <span className="min-w-0 truncate text-left" title={league.nameVN || league.name}>
                          {displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {leaguesByCountry.map((group) => (
                    <div key={group.country}>
                      <div className="box-border flex items-center gap-1.5 px-1 py-0.5 text-[11px] font-semibold uppercase text-[var(--theme-text-muted)]">
                        {group.countryLogo && (
                          <img
                            src={group.countryLogo}
                            alt={group.country}
                            className="block h-5 w-5 object-contain"
                          />
                        )}
                        <span>{group.country}</span>
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-1.5">
                        {group.items.map((league) => {
                          const checked = tempSelectedIds.includes(league.id);
                          const displayName = league.nameVN || league.name || "";
                          return (
                            <button
                              key={league.id}
                              type="button"
                              onClick={() => handleToggleLeague(league.id)}
                              className="appearance-none m-0 box-border flex min-w-0 items-center gap-1.5 rounded border border-[var(--theme-border)] bg-[var(--theme-bg)] py-1.5 pl-0.5 pr-1.5 text-left text-xs transition-colors hover:bg-[var(--theme-accent)]/50 cursor-pointer"
                            >
                              <span
                                className={`box-border flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[9px] font-bold ${
                                  checked
                                    ? "border-[#2563eb] bg-[#2563eb] text-white"
                                    : "border-[var(--theme-border)] bg-[var(--theme-foreground)]"
                                }`}
                              >
                                {checked ? "✓" : ""}
                              </span>
                              {league.logo && (
                                <img
                                  src={league.logo}
                                  alt=""
                                  className="block h-5 w-5 shrink-0 object-contain"
                                />
                              )}
                              <span className="min-w-0 truncate text-left" title={league.nameVN || league.name}>
                                {displayName}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="box-border flex shrink-0 items-center justify-between gap-2 border-t border-[var(--theme-border)] bg-[var(--theme-bg)] px-3 py-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="appearance-none m-0 p-0 bg-transparent border-none flex items-center gap-1.5 text-xs text-[var(--theme-text)] cursor-pointer"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold ${
                      filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id))
                        ? "border-[#2563eb] bg-[#2563eb] text-white"
                        : "border-[var(--theme-border)] bg-[var(--theme-foreground)]"
                    }`}
                  >
                    {filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id)) ? "✓" : ""}
                  </span>
                  Chọn tất cả
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="appearance-none m-0 p-0 bg-transparent border-none flex items-center gap-1.5 text-xs text-[var(--theme-text)] cursor-pointer"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold ${
                      tempSelectedIds.length === 0
                        ? "border-[#2563eb] bg-[#2563eb] text-white"
                        : "border-[var(--theme-border)] bg-[var(--theme-foreground)]"
                    }`}
                  >
                    {tempSelectedIds.length === 0 ? "✓" : ""}
                  </span>
                  Bỏ chọn tất cả
                </button>
              </div>
              <button
                type="button"
                onClick={handleConfirm}
                className="appearance-none m-0 box-border shrink-0 rounded-lg bg-[var(--theme-primary)] px-4 py-1.5 text-xs font-semibold text-[var(--theme-on-primary)] border-none cursor-pointer"
              >
                Xác nhận
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
